import { FC, useEffect, useMemo, useState } from 'react';

import logger from '@eneco/common/log';
import RichText from '@eneco/components/RichText/RichText';
import type { BoilerBrandDto, OrderGasMaintenanceDto, OrderSwitchReasonDto } from '@eneco/dc-be/client';
import { PreswitchBasicRecordDto, putEnecoBeXapiSiteApiV1OrdersByOrderIdGas } from '@eneco/dc-be/client';
import { unwrapValidationError } from '@eneco/dc-be/utils/unwrapError.js';
import {
  Bucket,
  Button,
  Checkbox,
  Heading,
  InputMeterReading,
  InputSelect,
  NotificationBox,
  Skeleton,
  Stack,
} from '@eneco/sparky';
import { Input } from '@eneco/sparky/components/Input/Input';
import { GasIcon } from '@eneco/sparky/icons';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { OrderManagementWithItems } from '@sitecore/types/manual/be/OrderManagementWithItems';
import { Controller, useFormContext } from 'react-hook-form';

import DataErrorNotification from '../../../components/DataErrorNotification';
import { InputDigits } from '../../../components/InputDigits';
import ReadFormField from '../../../components/ReadFormField';
import { useMonthInputSupport } from '../hooks/useMonthInputSupport';
import { useOrderId } from '../hooks/useOrderId';
import { usePreswitchBasicRecords } from '../hooks/usePreswitchBasicRecords';
import { useSelectedProducts } from '../hooks/useSelectedProducts';
import { OrderManagementFormSchema } from '../types/orderManagementSchema';
import { FlowChildProps } from '../types/OrderManagementSteps';
import {
  CHOOSE_START_DATE_QUESTION_NAME,
  NEW_METER_CONNECTION,
  START_DATE_QUESTION_ASAP,
  TAKE_OVER_METER_CONNECTION_OLD_INHABITANT,
} from '../utils/constants';
import { BOILER_INSTALLATION_DATE_QUESTION_TO_STATUS } from '../utils/getOrderManagementDefaultValues';
import { getTrackableFormValues } from '../utils/getTrackableFormValues';
import { toBackendMonthDate, toMonthInputValue } from '../utils/monthDateUtils';
import { getInstallationDateMax, getMaintenanceDateMin, getStartDateBounds } from '../utils/orderManagementDateBounds';

interface GasMeterInformationFieldsProps {
  fields: OrderManagementWithItems['fields'];
  matchedGasRecord?: PreswitchBasicRecordDto;
  isLoadingPreswitchRecords: boolean;
  meterTypeListOptions: Array<{ label: string; value: string }>;
  eanCodeOfMeterListOptions: Array<{ label: string; value: string }>;
  eanCodeOfMeter: string | undefined;
  meterReadingsKnown: boolean | undefined;
  showMeterReadingsKnownQuestion: boolean;
  showMeterReadingTotal: boolean;
  unknownMeterNumberOrEanCode?: { description: string };
}

const GasMeterInformationFields: FC<GasMeterInformationFieldsProps> = ({
  fields,
  matchedGasRecord,
  isLoadingPreswitchRecords,
  meterTypeListOptions,
  eanCodeOfMeterListOptions,
  eanCodeOfMeter,
  showMeterReadingsKnownQuestion,
  showMeterReadingTotal,
  unknownMeterNumberOrEanCode,
}) => {
  const {
    formState: { errors },
    register,
  } = useFormContext<OrderManagementFormSchema>();

  if (isLoadingPreswitchRecords) {
    return (
      <Stack gap={4}>
        <Skeleton height={100} />
        <Skeleton height={100} />
        <Skeleton height={100} />
      </Stack>
    );
  }

  if (matchedGasRecord) {
    return (
      <ReadFormField
        label={fields.gasDetailsStep.eanCodeFormField.value.label}
        value={matchedGasRecord.headpoint}
        isLoading={isLoadingPreswitchRecords}
      />
    );
  }

  return (
    <>
      <InputSelect
        label={fields.gasDetailsStep.meterTypeList.value.label}
        options={meterTypeListOptions ?? []}
        isOptional={false}
        error={errors.gasDetailsStep?.meterType?.message}
        placeholder={''}
        {...register('gasDetailsStep.meterType')}
      />
      <InputSelect
        label={fields.gasDetailsStep.eanCodeOfMeterList.value.label}
        options={eanCodeOfMeterListOptions ?? []}
        isOptional={false}
        error={errors.gasDetailsStep?.eanCodeOfMeter?.message}
        placeholder={''}
        hint={eanCodeOfMeter === 'Unknown' ? <u>{unknownMeterNumberOrEanCode?.description}</u> : null}
        {...register('gasDetailsStep.eanCodeOfMeter')}
      />
      {eanCodeOfMeter === 'EAN' && (
        <InputDigits
          label={fields.gasDetailsStep.eanCodeFormField.value.label}
          error={errors.gasDetailsStep?.eanCode?.message}
          placeholder={fields.gasDetailsStep.eanCodeFormField.value.placeholder}
          {...register('gasDetailsStep.eanCode')}
          hint={<RichText html={fields.gasDetailsStep.eanCodeFormField.value.hint} />}
        />
      )}
      {eanCodeOfMeter === 'MeterNumber' && (
        <Input
          label={fields.gasDetailsStep.meterNumberFormField.value.label}
          id="meterNumber"
          type="text"
          error={errors.gasDetailsStep?.meterNumber?.message}
          placeholder={fields.gasDetailsStep.meterNumberFormField.value.placeholder}
          {...register('gasDetailsStep.meterNumber')}
          hint={<RichText html={fields.gasDetailsStep.meterNumberFormField.value.hint} />}
        />
      )}
      {showMeterReadingsKnownQuestion && (
        <Checkbox
          label={fields.gasDetailsStep.meterReadingsKnownFormField.value.label}
          error={errors.gasDetailsStep?.meterReadingsKnown?.message}
          {...register('gasDetailsStep.meterReadingsKnown')}
          hint={<RichText html={fields.gasDetailsStep.meterReadingsKnownFormField.value.hint} />}
        />
      )}
      {showMeterReadingTotal && (
        <InputMeterReading
          label={fields.gasDetailsStep.meterReadingTotalFormField.value.label}
          unit="m3"
          maximumFractionDigits={0}
          error={errors.gasDetailsStep?.meterReadingTotal?.message}
          placeholder={fields.gasDetailsStep.meterReadingTotalFormField.value.placeholder}
          {...register('gasDetailsStep.meterReadingTotal')}
          hint={<RichText html={fields.gasDetailsStep.meterReadingTotalFormField.value.hint} />}
        />
      )}
    </>
  );
};

const getDeliveryStartDate = (
  startDate: string | Date | undefined,
  isStartDateQuestionAsap: boolean,
  reason: string,
): string | null => {
  switch (reason) {
    case 'MoveToEneco':
      return startDate ? (isStartDateQuestionAsap ? null : startDate.toString()) : null;
    case 'NewMeterConnection':
      return null;
    case 'TakeOverMeterConnectionOldInhabitant':
      return startDate ? startDate.toString() : null;
    default:
      return null;
  }
};

export const GasDetailsStep: FC<FlowChildProps> = ({ nextMainStep }) => {
  const { fields } = useContent<OrderManagementWithItems>();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [hasUpdateError, setHasUpdateError] = useState(false);
  const [hasEanAlreadyExistsError, setHasEanAlreadyExistsError] = useState(false);
  const orderId = useOrderId();
  const {
    register,
    control,
    setValue,
    trigger,
    formState: { errors },
    watch,
    getValues,
  } = useFormContext<OrderManagementFormSchema>();
  const { trackEventBE } = useTracking();

  const monthInputType = useMonthInputSupport();
  const { hasSelectedGasProduct, hasSelectedMaintenance } = useSelectedProducts({
    orderId,
  });
  const { matchedGasRecords, isLoading: isLoadingPreswitchRecords } = usePreswitchBasicRecords();
  const matchedGasRecord = matchedGasRecords.length === 1 ? matchedGasRecords[0] : undefined;
  const editReason = watch('gasDetailsStep.reason');
  const startDateBounds = useMemo(() => getStartDateBounds(editReason), [editReason]);
  const installationDateMax = useMemo(() => getInstallationDateMax(monthInputType), [monthInputType]);
  const maintenanceDateMin = useMemo(() => getMaintenanceDateMin(monthInputType), [monthInputType]);
  const startDateQuestion = watch('gasDetailsStep.startDateQuestion');
  const meterType = watch('gasDetailsStep.meterType');
  const eanCodeOfMeter = watch('gasDetailsStep.eanCodeOfMeter');
  const meterReadingsKnown = watch('gasDetailsStep.meterReadingsKnown');
  const boilerInstallationDateQuestion = watch('gasDetailsStep.boilerInstallationDateQuestion');
  const boilerServiceOption = watch('gasDetailsStep.boilerServiceOption');

  const showMeterReadingsKnownQuestion =
    editReason === 'TakeOverMeterConnectionOldInhabitant' && meterType === 'Analogue';
  const showMeterReadingTotal = showMeterReadingsKnownQuestion && meterReadingsKnown && meterType === 'Analogue';

  useEffect(() => {
    setValue('gasDetailsStep.showBoilerSpecifications', hasSelectedMaintenance, { shouldValidate: false });
  }, [setValue, hasSelectedMaintenance]);

  const editReasonListOptions = fields.gasDetailsStep.editReasonList?.value.options.map(option => ({
    label: option?.label,
    value: option.name,
  }));

  const startDateQuestionOptions = fields.gasDetailsStep.startDateQuestionList?.value.options.map(option => ({
    label: option?.label,
    value: option.name,
  }));

  const editReasonHint = fields.gasDetailsStep.editReasonList?.value.options.find(
    option => option.name === 'NewMeterConnection',
  )?.description;

  const unknownMeterNumberOrEanCode = fields.gasDetailsStep.eanCodeOfMeterList?.value.options.find(
    option => option.name === 'Unknown',
  );

  const meterTypeListOptions = fields.gasDetailsStep.meterTypeList?.value.options
    .map(option => ({
      label: option?.label,
      value: option.name,
    }))
    .filter(option => option.value !== 'BiDirectional');

  const eanCodeOfMeterListOptions = fields.gasDetailsStep.eanCodeOfMeterList?.value.options.map(option => ({
    label: option?.label,
    value: option.name,
  }));

  const boilerBrandOptions = fields.gasDetailsStep.boilerBrandsList?.value.options.map(option => ({
    label: option?.label,
    value: option.name as BoilerBrandDto,
  }));

  const boilerInstallationDateQuestionOptions =
    fields.gasDetailsStep.boilerInstallationDateQuestionList?.value.options.map(option => ({
      label: option?.label,
      value: option.name,
    }));

  const boilerServiceOptions = fields.gasDetailsStep.boilerServiceOptionsList?.value.options.map(option => ({
    label: option?.label,
    value: option.name,
  }));

  const boilerInstallationDateYesOptionName =
    fields.gasDetailsStep.boilerInstallationDateQuestionList?.value.options.find(option => option.name === 'Yes')
      ?.name ?? fields.gasDetailsStep.boilerInstallationDateQuestionList?.value.options[0]?.name;

  const boilerServiceChooseDateOptionName = fields.gasDetailsStep.boilerServiceOptionsList?.value.options[1]?.name;

  const onNextClick = async () => {
    setIsLoadingSubmit(true);
    const isValid = await trigger('gasDetailsStep');
    if (!isValid) {
      setIsLoadingSubmit(false);
      return;
    }

    const values = getValues('gasDetailsStep');
    const deliveryStartDateAsap =
      values.startDateQuestion === START_DATE_QUESTION_ASAP && values.reason === 'MoveToEneco';
    const isNewMeterConnection = values.reason === NEW_METER_CONNECTION;
    const deliveryStartDate = getDeliveryStartDate(
      values.startDate as string | Date | undefined,
      deliveryStartDateAsap,
      values.reason as string,
    );

    const installationStatus =
      values.boilerInstallationDateQuestion != null &&
      values.boilerInstallationDateQuestion in BOILER_INSTALLATION_DATE_QUESTION_TO_STATUS
        ? BOILER_INSTALLATION_DATE_QUESTION_TO_STATUS[
            values.boilerInstallationDateQuestion as keyof typeof BOILER_INSTALLATION_DATE_QUESTION_TO_STATUS
          ]
        : 'NotInstalled';
    const installationDate =
      installationStatus === 'Installed' && values.installationDate != null
        ? toBackendMonthDate(values.installationDate)
        : null;
    const maintenanceStartDate =
      values.boilerServiceOption !== START_DATE_QUESTION_ASAP && values.maintenanceDate != null
        ? toBackendMonthDate(values.maintenanceDate)
        : null;
    const boilerServiceDate = maintenanceStartDate;

    const maintenance: OrderGasMaintenanceDto | undefined =
      hasSelectedMaintenance && values.boilerBrand
        ? {
            brand: values.boilerBrand as BoilerBrandDto,
            buildingOlderThan10Years: values.homeOlderThanTenYears,
            installationStatus,
            installationDate,
            boilerServiceDate,
            maintenanceStartDate,
            maintenanceStartAsap: values.boilerServiceOption === START_DATE_QUESTION_ASAP,
          }
        : undefined;

    if (hasSelectedGasProduct || hasSelectedMaintenance) {
      if (!orderId) {
        setIsLoadingSubmit(false);
        return;
      }
      try {
        const meterNumber = values.eanCodeOfMeter === 'MeterNumber' ? values.meterNumber : '';
        const ean = matchedGasRecord?.headpoint || (values.eanCodeOfMeter === 'EAN' ? values.eanCode : undefined);
        const { response, error } = await putEnecoBeXapiSiteApiV1OrdersByOrderIdGas({
          path: { orderId },
          body: {
            ...(hasSelectedGasProduct && {
              switchReason: values.reason as OrderSwitchReasonDto | undefined,
              meterNumber,
              ean,
              meterReading:
                showMeterReadingTotal && values.meterReadingTotal != null
                  ? { meterTimeFrameType: 'TH', value: values.meterReadingTotal }
                  : undefined,
              deliveryStartDateAsap,
              deliveryStartDate: deliveryStartDateAsap || isNewMeterConnection ? null : deliveryStartDate,
            }),
            maintenance,
          },
        });
        if (!response.ok) {
          logger.error('GasDetailsStep', 'Failed to update gas details', { response });
          const validationErrors = unwrapValidationError(error);
          if (validationErrors.includes('Order.EanAlreadyExists')) {
            setHasEanAlreadyExistsError(true);
          } else {
            setHasUpdateError(true);
          }
          window.scrollTo({ top: 0, behavior: 'smooth' });
          setIsLoadingSubmit(false);
          trackEventBE('be_om_gas_details', {
            status: 'failed',
            message: 'Failed to update order with gas details',
            data: getTrackableFormValues(getValues()),
          });
          return;
        }
      } catch (error) {
        const validationErrors = unwrapValidationError(error as Parameters<typeof unwrapValidationError>[0]);
        if (validationErrors.includes('Order.EanAlreadyExists')) {
          setHasEanAlreadyExistsError(true);
        } else {
          setHasUpdateError(true);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setIsLoadingSubmit(false);
        trackEventBE('be_om_gas_details', {
          status: 'failed',
          message: 'Failed to update order with gas details',
          data: getTrackableFormValues(getValues()),
        });
        return;
      }
    }
    trackEventBE('be_om_gas_details', {
      status: 'success',
      data: getTrackableFormValues(getValues()),
    });
    nextMainStep();
    setIsLoadingSubmit(false);
  };

  return (
    <Stack gap="10" alignX="start">
      {hasUpdateError && <DataErrorNotification />}
      {hasEanAlreadyExistsError && (
        <NotificationBox
          isAlert={false}
          variant={fields.gasDetailsStep?.duplicateEanNotification?.value?.variant ?? 'error'}
          title={fields.gasDetailsStep?.duplicateEanNotification?.value?.title}
          text={fields.gasDetailsStep?.duplicateEanNotification?.value?.content}
        />
      )}
      <Stack gap="2">
        <Heading as="h2" size="M">
          {hasSelectedGasProduct ? fields.gasDetailsStep.title.value : fields.gasDetailsStep.maintenanceTitle.value}
        </Heading>
        <RichText html={fields.gasDetailsStep.subTitle.value} />
      </Stack>
      {hasSelectedGasProduct && (
        <>
          <Bucket title={fields.gasDetailsStep.dateAndReasonTitle.value}>
            <Bucket.Content>
              <InputSelect
                label={fields.gasDetailsStep.editReasonList.value.label}
                options={editReasonListOptions ?? []}
                isOptional={false}
                error={errors.gasDetailsStep?.reason?.message}
                placeholder={''}
                hint={editReason === 'NewMeterConnection' ? <RichText html={editReasonHint} /> : null}
                {...register('gasDetailsStep.reason')}
              />
              {editReason === 'MoveToEneco' && (
                <InputSelect
                  label={fields.gasDetailsStep.startDateQuestionList.value.label}
                  options={startDateQuestionOptions ?? []}
                  isOptional={false}
                  error={errors.gasDetailsStep?.startDateQuestion?.message}
                  placeholder={''}
                  {...register('gasDetailsStep.startDateQuestion')}
                />
              )}
              {((startDateQuestion === CHOOSE_START_DATE_QUESTION_NAME && editReason !== NEW_METER_CONNECTION) ||
                editReason === TAKE_OVER_METER_CONNECTION_OLD_INHABITANT) && (
                <Input
                  label={fields.gasDetailsStep.startDateFormField.value.label}
                  id="startDate"
                  type="date"
                  min={startDateBounds.min}
                  max={startDateBounds.max}
                  error={errors.gasDetailsStep?.startDate?.message}
                  {...register('gasDetailsStep.startDate')}
                  autoComplete="off"
                />
              )}
            </Bucket.Content>
          </Bucket>
          <Bucket
            title={
              <Stack gap="2" direction="row" alignY="center">
                <GasIcon color="iconGas" />
                <Heading as="h3" size="3XS">
                  {fields.gasDetailsStep.gasMeterInformationTitle.value}
                </Heading>
              </Stack>
            }>
            <Bucket.Content>
              <GasMeterInformationFields
                fields={fields}
                matchedGasRecord={matchedGasRecord}
                isLoadingPreswitchRecords={isLoadingPreswitchRecords}
                meterTypeListOptions={meterTypeListOptions ?? []}
                eanCodeOfMeterListOptions={eanCodeOfMeterListOptions ?? []}
                eanCodeOfMeter={eanCodeOfMeter}
                meterReadingsKnown={meterReadingsKnown}
                showMeterReadingsKnownQuestion={showMeterReadingsKnownQuestion}
                showMeterReadingTotal={showMeterReadingTotal}
                unknownMeterNumberOrEanCode={unknownMeterNumberOrEanCode}
              />
            </Bucket.Content>
          </Bucket>
        </>
      )}
      {hasSelectedMaintenance && (
        <Bucket
          title={
            <Stack gap="2" direction="row" alignY="center">
              <GasIcon color="iconGas" />
              <Heading as="h3" size="3XS">
                {fields.gasDetailsStep.boilerInformationTitle.value}
              </Heading>
            </Stack>
          }>
          <Bucket.Content>
            <InputSelect
              label={fields.gasDetailsStep.boilerBrandsList.value.label}
              options={boilerBrandOptions ?? []}
              isOptional={false}
              error={errors.gasDetailsStep?.boilerBrand?.message}
              placeholder={''}
              {...register('gasDetailsStep.boilerBrand')}
            />
            <InputSelect
              label={fields.gasDetailsStep.boilerInstallationDateQuestionList.value.label}
              options={boilerInstallationDateQuestionOptions ?? []}
              isOptional={false}
              error={errors.gasDetailsStep?.boilerInstallationDateQuestion?.message}
              placeholder={''}
              {...register('gasDetailsStep.boilerInstallationDateQuestion')}
            />
            {boilerInstallationDateQuestion &&
              boilerInstallationDateQuestion === boilerInstallationDateYesOptionName && (
                <Controller
                  name="gasDetailsStep.installationDate"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label={fields.gasDetailsStep.installationDateFormField.value.label}
                      id="installationDate"
                      type={monthInputType}
                      max={installationDateMax}
                      error={errors.gasDetailsStep?.installationDate?.message}
                      {...field}
                      value={
                        monthInputType === 'month' ? toMonthInputValue(field.value) : (field.value as unknown as string)
                      }
                      autoComplete="off"
                    />
                  )}
                />
              )}
            <InputSelect
              label={fields.gasDetailsStep.boilerServiceOptionsList.value.label}
              options={boilerServiceOptions ?? []}
              isOptional={false}
              error={errors.gasDetailsStep?.boilerServiceOption?.message}
              placeholder={''}
              {...register('gasDetailsStep.boilerServiceOption')}
            />
            {boilerServiceOption && boilerServiceOption === boilerServiceChooseDateOptionName && (
              <Controller
                name="gasDetailsStep.maintenanceDate"
                control={control}
                render={({ field }) => (
                  <Input
                    label={fields.gasDetailsStep.maintenanceDateFormField.value.label}
                    id="maintenanceDate"
                    type={monthInputType}
                    min={maintenanceDateMin}
                    error={errors.gasDetailsStep?.maintenanceDate?.message}
                    {...field}
                    value={
                      monthInputType === 'month' ? toMonthInputValue(field.value) : (field.value as unknown as string)
                    }
                    autoComplete="off"
                  />
                )}
              />
            )}
            <Checkbox
              label={fields.gasDetailsStep.homeOlderThanTenYearsFormField.value.label}
              error={errors.gasDetailsStep?.homeOlderThanTenYears?.message}
              hint={<RichText html={fields.gasDetailsStep.homeOlderThanTenYearsFormField.value.hint} />}
              {...register('gasDetailsStep.homeOlderThanTenYears')}
            />
          </Bucket.Content>
        </Bucket>
      )}
      <Button onClick={onNextClick} isLoading={isLoadingSubmit}>
        {fields.navigation.nextButtonLabel.value}
      </Button>
    </Stack>
  );
};
